import { FairUseCreditsUsage } from "@app/components/app/FairUseCreditsUsage";
import { TrialMessageUsage } from "@app/components/app/TrialMessageUsage";
import { SidebarBanners } from "@app/components/navigation/AppStatusBanner";
import type { SidebarNavigation } from "@app/components/navigation/config";
import { isAdminNavigationRoute } from "@app/components/navigation/config";
import { useDesktopNavigation } from "@app/components/navigation/DesktopNavigationContext";
import { useNavigationPanelSection } from "@app/components/navigation/NavigationRail";
import { PanelCreateButton } from "@app/components/navigation/PanelCreateButton";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { FREE_TRIAL_PHONE_PLAN_CODE } from "@app/lib/plans/plan_codes";
import { useAppRouter } from "@app/lib/platform";
import type { SubscriptionType } from "@app/types/plan";
import type { UserTypeWithWorkspaces, WorkspaceType } from "@app/types/user";
import { isAdmin } from "@app/types/user";
import {
  Button,
  CollapseButton,
  LayoutLeft,
  SearchMd,
  XClose,
} from "@dust-tt/sparkle";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import React, { useCallback, useContext, useMemo } from "react";

const panelSectionTitles = {
  dms: "Direct messages",
  projects: "Projects",
  contacts: "Contacts",
} as const;

interface NavigationSidebarProps {
  children: React.ReactNode;
  owner: WorkspaceType;
  subNavigation?: SidebarNavigation[] | null;
  // TODO(2024-06-19 flav) Move subscription to a hook.
  subscription: SubscriptionType;
  user: (UserTypeWithWorkspaces & { subscriberHash?: string | null }) | null;
  isMobile?: boolean;
}

export const NavigationSidebar = React.forwardRef<
  HTMLDivElement,
  NavigationSidebarProps
>(function NavigationSidebar(
  { owner, subscription, children, isMobile }: NavigationSidebarProps,
  ref
) {
  const router = useAppRouter();
  const activePath = useMemo(() => {
    if (router.isReady && router.pathname) {
      return router.pathname;
    }
    return "";
  }, [router.isReady, router.pathname]);

  const isSettingsRoute = isAdminNavigationRoute(activePath);

  const { setSidebarOpen } = useContext(SidebarContext);
  const { setIsNavigationBarOpen } = useDesktopNavigation();
  const { panelSection } = useNavigationPanelSection(owner);
  const reduceMotion = useReducedMotion();

  return (
    <div ref={ref} className="sheet flex h-full min-h-0 min-w-0 grow flex-col">
      <div className="sheet-header relative z-10 flex shrink-0 flex-col gap-2 border-b border-border bg-app-background pb-2 pt-2 dark:border-border-night">
        <SidebarBanners />
        <div className="mx-sidebar-side-spacing flex h-title items-center justify-between gap-2 pl-1">
          <AnimatePresence mode="wait" initial={false}>
            <motion.h2
              key={panelSection}
              initial={reduceMotion ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="min-w-0 flex-1 truncate text-lg font-semibold tracking-tight text-foreground"
            >
              {panelSectionTitles[panelSection]}
            </motion.h2>
          </AnimatePresence>
          <div className="flex shrink-0 items-center gap-0.5">
            <PanelCreateButton
              owner={owner}
              subscription={subscription}
              section={panelSection}
            />
            <Button
              variant="ghost"
              size="sm"
              icon={SearchMd}
              className="rounded-lg"
              tooltip="Search"
              onClick={() => {
                window.dispatchEvent(new CustomEvent("memoair-sidebar-search"));
              }}
            />
            {!isMobile && (
              <Button
                variant="ghost"
                size="sm"
                icon={LayoutLeft}
                className="rounded-lg"
                tooltip="Collapse sidebar"
                onClick={() => setIsNavigationBarOpen(false)}
              />
            )}
            {isMobile && (
              <Button
                variant="ghost"
                size="sm"
                icon={XClose}
                className="rounded-lg"
                tooltip="Close"
                onClick={() => setSidebarOpen(false)}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {!isSettingsRoute && (
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        )}
      </div>
      {subscription.plan.code === FREE_TRIAL_PHONE_PLAN_CODE ? (
        <div className="mx-3 mb-3 shrink-0">
          <TrialMessageUsage isAdmin={isAdmin(owner)} workspaceId={owner.sId} />
        </div>
      ) : (
        // Only mount when the plan has a fair-use credits limit so that the
        // fair-use-credits endpoint is never called for unlimited plans.
        subscription.plan.limits.assistant.maxAwuCredits !== -1 && (
          <div className="shrink-0">
            <FairUseCreditsUsage workspaceId={owner.sId} />
          </div>
        )
      )}
    </div>
  );
});

interface ToggleNavigationSidebarButtonProps {
  isNavigationBarOpened: boolean;
  isFullScreen: boolean;
  toggleNavigationBarVisibility: (isOpened: boolean) => void;
}

export const ToggleNavigationSidebarButton = React.forwardRef<
  HTMLDivElement,
  ToggleNavigationSidebarButtonProps
>(function ToggleSideBarButton(
  {
    isNavigationBarOpened,
    isFullScreen,
    toggleNavigationBarVisibility,
  }: ToggleNavigationSidebarButtonProps,
  ref
) {
  const handleClick = useCallback(() => {
    toggleNavigationBarVisibility(!isNavigationBarOpened);
  }, [isNavigationBarOpened, toggleNavigationBarVisibility]);

  if (isNavigationBarOpened || isFullScreen) {
    return null;
  }

  return (
    <div ref={ref} onClick={handleClick} className="lg:top-1/2 lg:flex lg:w-5">
      <CollapseButton
        direction={isNavigationBarOpened ? "left" : "right"}
        variant="light"
      />
    </div>
  );
});
